import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { msalInstance } from './auth/msal';
import App from './App';
import logoIcon from './logo_icon.png';
import './index.css';

// Set the favicon from the bundled icon (hashed asset; no duplicate file).
const favicon = document.createElement('link');
favicon.rel = 'icon';
favicon.type = 'image/png';
favicon.href = logoIcon;
document.head.appendChild(favicon);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

async function bootstrap(): Promise<void> {
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0]);

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Missing #root element');

  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

/** Render a fatal startup error into #root instead of leaving a blank page. */
function renderFatal(error: unknown): void {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  const rootEl = document.getElementById('root');
  if (rootEl) {
    rootEl.innerHTML =
      '<div style="padding:2rem;font-family:Consolas,monospace;color:#1f2330">' +
      '<h1 style="color:#b91c1c;font-size:1.1rem;margin:0 0 .5rem">FABS failed to start</h1>' +
      '<pre style="white-space:pre-wrap;background:#fff;border:1px solid #e5e7eb;padding:1rem;border-radius:4px">' +
      message.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c] as string) +
      '</pre><p style="font-size:.85rem;color:#6b7280">See the browser console for the full stack.</p></div>';
  }
  // eslint-disable-next-line no-console
  console.error('FABS bootstrap failed:', error);
}

void bootstrap().catch(renderFatal);
